// test
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { debug } from "../../lib/debug.js";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { createNegotiationData } from "../negotiations/NegotiationController.jsx";
import { AIRCRAFT_DATABASE } from "../../data/aircraft.js";
import { calculateTier } from "../../systems/ReputationEngine.js";
import {
  createEOSState,
  rollRareAircraft,
  processA380Purchase,
  getEOSStatus,
  EOS_REQUIREMENTS,
  ROLL_PROBABILITIES,
  RARE_AIRCRAFT,
} from "../../systems/EOSSystem.js";
import { generateTrailerFlights } from "../../systems/TrailerMode.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import MythicReveal from "../MythicReveal.jsx";
import { soundManager, SOUNDS } from "../../systems/SoundManager.js";
// Icons removed - text-only UI

// ============================================================================
// CHEAT CONSOLE - Developer Testing Panel
// ============================================================================
// Toggle: Backtick (`) key or Konami Code
// ============================================================================

const KONAMI_CODE = [
  "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight",
  "KeyB", "KeyA"
];

export default function CheatConsole() {
  const {
    worldState,
    setWorldState,
    updateCash,
    addAircraft,
    startNegotiation,
    resetWorldState,
  } = useWorldState();

  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const konamiIndex = useRef(0);
  const panelRef = useRef(null);

  // Input states
  const [cashInput, setCashInput] = useState("");
  const [wingsInput, setWingsInput] = useState("");
  const [dateInput, setDateInput] = useState("");
  const [slotAirport, setSlotAirport] = useState("KSMF");
  const [triggerAirport, setTriggerAirport] = useState("KSMF");
  const [selectedEOSAircraft, setSelectedEOSAircraft] = useState("");

  // Test roll animation state
  const [testRollAircraft, setTestRollAircraft] = useState(null);
  const [showTestRoll, setShowTestRoll] = useState(false);

  // Get all EOS collectibles grouped by tier
  const eosCollectibles = React.useMemo(() => {
    const collectibles = Object.entries(AIRCRAFT_DATABASE)
      .filter(([_, ac]) => ac.category === "collectible")
      .map(([id, ac]) => ({ id, ...ac }));

    return {
      rare: collectibles.filter(a => a.rarity === "rare").sort((a, b) => a.model.localeCompare(b.model)),
      relic: collectibles.filter(a => a.rarity === "relic").sort((a, b) => a.model.localeCompare(b.model)),
      anomalous: collectibles.filter(a => a.rarity === "anomalous").sort((a, b) => a.model.localeCompare(b.model)),
      legendary: collectibles.filter(a => a.rarity === "legendary").sort((a, b) => a.model.localeCompare(b.model)),
      mythic: collectibles.filter(a => a.rarity === "mythic").sort((a, b) => a.model.localeCompare(b.model)),
      total: collectibles.length,
    };
  }, []);

  // ============================================================================
  // KEYBOARD SHORTCUTS
  // ============================================================================

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cheat console disabled for release
      // if (e.code === "Backquote" && !e.target.matches("input, textarea")) {
      //   e.preventDefault();
      //   setIsOpen(prev => !prev);
      //   return;
      // }

      // Konami code detection
      if (e.code === KONAMI_CODE[konamiIndex.current]) {
        konamiIndex.current++;
        if (konamiIndex.current === KONAMI_CODE.length) {
          setIsOpen(true);
          konamiIndex.current = 0;
          debug("[DEV] Konami code activated!");
        }
      } else {
        konamiIndex.current = 0;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ============================================================================
  // DRAG HANDLING
  // ============================================================================

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest(".drag-handle")) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y)),
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ============================================================================
  // MONEY COMMANDS
  // ============================================================================

  const addMoney = (amount) => {
    updateCash(amount);
  };

  const setMoney = () => {
    const amount = parseFloat(cashInput);
    if (!isNaN(amount)) {
      setWorldState(prev => ({
        ...prev,
        airline: { ...prev.airline, cash: amount },
      }));
      setCashInput("");
    }
  };

  // ============================================================================
  // WINGS COMMANDS
  // ============================================================================

  const addWings = (amount) => {
    setWorldState(prev => {
      const currentWings = prev.wings || { balance: 20, lifetimeEarned: 20, lifetimeSpent: 0, flightsSinceLastWing: 0 };
      return {
        ...prev,
        wings: {
          ...currentWings,
          balance: Math.max(0, (currentWings.balance || 0) + amount),
          lifetimeEarned: amount > 0 ? (currentWings.lifetimeEarned || 0) + amount : currentWings.lifetimeEarned,
        },
      };
    });
  };

  const setWings = () => {
    const amount = parseInt(wingsInput);
    if (!isNaN(amount) && amount >= 0) {
      setWorldState(prev => {
        const currentWings = prev.wings || { balance: 20, lifetimeEarned: 20, lifetimeSpent: 0, flightsSinceLastWing: 0 };
        return {
          ...prev,
          wings: {
            ...currentWings,
            balance: amount,
          },
        };
      });
      setWingsInput("");
    }
  };

  // ============================================================================
  // TIME COMMANDS
  // ============================================================================

  const skipTime = (days) => {
    setWorldState(prev => {
      const newTime = new Date(prev.gameTime);
      newTime.setDate(newTime.getDate() + days);
      return {
        ...prev,
        gameTime: newTime.toISOString(),
        dayNumber: prev.dayNumber + days,
      };
    });
  };

  const setDate = () => {
    const date = new Date(dateInput);
    if (!isNaN(date.getTime())) {
      setWorldState(prev => {
        const startDate = new Date(prev.gameStartDate || prev.gameTime);
        const dayNumber = Math.floor((date - startDate) / (24 * 60 * 60 * 1000)) + 1;
        return {
          ...prev,
          gameTime: date.toISOString(),
          dayNumber: Math.max(1, dayNumber),
        };
      });
      setDateInput("");
    }
  };

  // Trigger end of day modal (for testing Day 1 to Day 2 negotiation flow)
  const triggerEndOfDay = () => {
    debug("[CheatConsole] Triggering end of day");
    // First clear any stale state, then set pendingEndOfDay
    // Note: endOfDayHandled should be false so the detection doesn't block
    setWorldState(prev => ({
      ...prev,
      pendingEndOfDay: true,
      endOfDayHandled: false, // Must be false - true would prevent future detection
      isPaused: true,
    }));
  };

  // ============================================================================
  // REPUTATION COMMANDS
  // ============================================================================
  // RepScore uses 0-15000+ scale, NOT 0-100
  // Tier thresholds: T1(0), T2(1000), T3(3000), T4(7000), T5(15000)

  const [repScoreInput, setRepScoreInput] = useState("");

  const adjustRepScore = (delta) => {
    setWorldState(prev => ({
      ...prev,
      reputation: {
        ...prev.reputation,
        currentScore: Math.max(0, (prev.reputation?.currentScore || 0) + delta),
        currentTier: calculateTierFromScore((prev.reputation?.currentScore || 0) + delta),
      },
    }));
  };

  const setRepScore = (score) => {
    const numScore = typeof score === 'number' ? score : parseInt(score);
    if (isNaN(numScore)) return;
    setWorldState(prev => ({
      ...prev,
      reputation: {
        ...prev.reputation,
        currentScore: Math.max(0, numScore),
        currentTier: calculateTierFromScore(numScore),
      },
    }));
  };

  const setRepScoreFromInput = () => {
    const score = parseInt(repScoreInput);
    if (!isNaN(score)) {
      setRepScore(score);
      setRepScoreInput("");
    }
  };

  // Calculate tier from RepScore
  const calculateTierFromScore = (score) => {
    if (score >= 15000) return 5;
    if (score >= 7000) return 4;
    if (score >= 3000) return 3;
    if (score >= 1000) return 2;
    return 1;
  };

  const setTier = (tier) => {
    // Set RepScore to tier minimum
    const scores = { 1: 0, 2: 1000, 3: 3000, 4: 7000, 5: 15000 };
    setRepScore(scores[tier] || 0);
  };

  // ============================================================================
  // FLEET COMMANDS
  // ============================================================================

  const addTestAircraft = (type) => {
    const dbEntry = AIRCRAFT_DATABASE[type];
    const registration = `N${Math.floor(Math.random() * 900) + 100}${type.slice(0, 2).toUpperCase()}`;

    const aircraft = {
      id: `AC-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      type,
      registration,
      name: dbEntry?.name || type,
      capacity: dbEntry?.capacity || 150,
      range: dbEntry?.range || 2000,
      cruiseSpeed: dbEntry?.cruiseSpeed || 450,
      fuelBurn: dbEntry?.fuelBurn || 2500,
      status: "available",
      location: worldState?.airline?.hub || "KPDX",
      homeBase: worldState?.airline?.hub || "KPDX",
      flightHours: 0,
      cycles: 0,
      leased: false,
      purchasePrice: dbEntry?.purchasePrice || 50000000,
      acquiredAt: new Date().toISOString(),
    };

    addAircraft(aircraft);
  };

  const removeAllAircraft = () => {
    setWorldState(prev => ({
      ...prev,
      aircraft: [],
    }));
  };

  // ============================================================================
  // SLOT COMMANDS
  // ============================================================================

  // Reset slots to default starter slots
  const resetSlotsToDefault = () => {
    setWorldState(prev => ({
      ...prev,
      slots: {
        KPDX: [
          { slotId: "KPDX-A", acquiredAt: "2025-01-15T00:00:00Z", monthlyFee: 0, expiresAt: null, isHubSlot: true },
          { slotId: "KPDX-B", acquiredAt: "2025-01-15T00:00:00Z", monthlyFee: 0, expiresAt: null, isHubSlot: true },
          { slotId: "KPDX-C", acquiredAt: "2025-01-15T00:00:00Z", monthlyFee: 0, expiresAt: null, isHubSlot: true },
        ],
        KSEA: [
          { slotId: "KSEA-A", acquiredAt: "2025-01-15T00:00:00Z", monthlyFee: 15000, expiresAt: null, isStarterSlot: true },
        ],
        KBOI: [
          { slotId: "KBOI-A", acquiredAt: "2025-01-15T00:00:00Z", monthlyFee: 10000, expiresAt: null, isStarterSlot: true },
        ],
        KRDM: [
          { slotId: "KRDM-A", acquiredAt: "2025-01-15T00:00:00Z", monthlyFee: 5000, expiresAt: null, isStarterSlot: true },
        ],
      },
      unlockedAirports: {
        KPDX: { unlockReason: "hub", unlockedAt: "2025-01-15T00:00:00Z" },
        KSEA: { unlockReason: "tier1_starter", unlockedAt: "2025-01-15T00:00:00Z" },
        KBOI: { unlockReason: "tier1_starter", unlockedAt: "2025-01-15T00:00:00Z" },
        KRDM: { unlockReason: "tier1_starter", unlockedAt: "2025-01-15T00:00:00Z" },
        KSMF: { unlockReason: "tier1_starter", unlockedAt: "2025-01-15T00:00:00Z" },
      },
    }));
    debug("[CHEAT] Slots reset to defaults: KPDX, KSEA, KBOI, KRDM");
  };

  const grantSlot = () => {
    if (!slotAirport) return;
    const code = slotAirport.toUpperCase();

    setWorldState(prev => {
      const newSlots = { ...prev.slots };
      const existing = newSlots[code] || [];
      existing.push({
        slotId: `${code}-${String.fromCharCode(65 + existing.length)}`,
        acquiredAt: new Date().toISOString(),
        monthlyFee: 30000,
        expiresAt: null,
        lastFlightDate: null,
        firstFlightDeadline: null,
        grantedByCheat: true,
      });
      newSlots[code] = existing;
      return { ...prev, slots: newSlots };
    });
  };

  const unlockAllAirports = () => {
    // Toggle airportsGated - when false, all 400+ airports are accessible
    setWorldState(prev => ({ ...prev, airportsGated: !prev.airportsGated }));
  };

  const triggerNegotiation = () => {
    const code = triggerAirport.toUpperCase();
    const negData = createNegotiationData(
      code,
      `${code} International Airport`,
      worldState?.airline?.hub || "KPDX",
      90,
      worldState?.relations // Pass relations for AI bid generation
    );
    startNegotiation(negData);
  };

  // Force trigger a hub auction (for testing)
  const triggerHubAuction = () => {
    const code = triggerAirport.toUpperCase() || 'KSLC';
    const existingHubs = worldState?.airline?.hubs || ['KPDX'];
    const pendingInvites = worldState?.pendingHubInvites || [];

    if (existingHubs.includes(code)) {
      debug(`[Cheat] Already own hub: ${code}`);
      return;
    }

    setWorldState(prev => ({
      ...prev,
      pendingHubInvites: [...(prev.pendingHubInvites || []).filter(h => h !== code), code],
      hubCapacityNotified: {}, // Reset capacity notification so it can re-trigger
    }));
    debug(`[Cheat] Hub auction triggered for: ${code} - go to WorldMap to see it!`);
  };

  // ============================================================================
  // SIMULATION COMMANDS
  // ============================================================================

  const forceFlightComplete = () => {
    setWorldState(prev => {
      const activeFlights = prev.flights.filter(f =>
        f.phase !== "completed" && f.phase !== "cancelled" && f.phase !== "scheduled"
      );
      if (activeFlights.length === 0) return prev;

      const flight = activeFlights[0];
      return {
        ...prev,
        flights: prev.flights.map(f =>
          f.id === flight.id
            ? { ...f, phase: "completed", status: "completed", revenue: 50000, costs: 30000 }
            : f
        ),
        stats: {
          ...prev.stats,
          completedFlights: (prev.stats?.completedFlights || 0) + 1,
        },
      };
    });
  };

  const triggerRandomDelay = () => {
    setWorldState(prev => {
      const activeFlights = prev.flights.filter(f =>
        f.phase !== "completed" && f.phase !== "cancelled"
      );
      if (activeFlights.length === 0) return prev;

      const flight = activeFlights[Math.floor(Math.random() * activeFlights.length)];
      const delayMinutes = Math.floor(Math.random() * 60) + 15;

      return {
        ...prev,
        flights: prev.flights.map(f =>
          f.id === flight.id
            ? {
                ...f,
                totalDelayMinutes: (f.totalDelayMinutes || 0) + delayMinutes,
                delays: [...(f.delays || []), { minutes: delayMinutes, reason: "Cheat console", timestamp: new Date().toISOString() }]
              }
            : f
        ),
        newsFeed: [
          { type: "warning", text: `[CHEAT] ${flight.flightNumber} delayed +${delayMinutes}min` },
          ...prev.newsFeed.slice(0, 9),
        ],
      };
    });
  };

  const triggerCrewIllegality = () => {
    setWorldState(prev => ({
      ...prev,
      crewDuty: {
        ...prev.crewDuty,
        pilots: {
          ...prev.crewDuty?.pilots,
          cheat_pilot_1: {
            name: "Test Pilot",
            hoursToday: 12,
            hoursThisWeek: 65,
            hoursThisMonth: 105,
            status: "illegal",
          },
        },
      },
      newsFeed: [
        { type: "critical", text: "[CHEAT] CREW ILLEGAL: Test Pilot exceeded limits" },
        ...prev.newsFeed.slice(0, 9),
      ],
    }));
  };

  const triggerWeatherEvent = () => {
    setWorldState(prev => {
      const airports = Object.keys(prev.weather?.airportConditions || {});
      const targetAirport = airports[Math.floor(Math.random() * airports.length)] || "KPDX";

      return {
        ...prev,
        weather: {
          ...prev.weather,
          airportConditions: {
            ...prev.weather?.airportConditions,
            [targetAirport]: {
              clear: false,
              conditions: "IFR",
              weather: "Thunderstorm",
              weatherIcon: "TSTM",
              intensity: 0.8,
              groundStop: true,
              delays: { probability: 0.9, minutes: 45 },
            },
          },
        },
        newsFeed: [
          { type: "critical", text: `[CHEAT] GROUND STOP at ${targetAirport} - Thunderstorm` },
          ...prev.newsFeed.slice(0, 9),
        ],
      };
    });
  };

  // ============================================================================
  // STATE COMMANDS
  // ============================================================================

  const exportState = () => {
    const dataStr = JSON.stringify(worldState, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `airline-ops-state-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importState = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const state = JSON.parse(ev.target.result);
          setWorldState(state);
          debug("State imported successfully");
        } catch (err) {
          console.error("Failed to import state:", err);
          alert("Failed to import state: " + err.message);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  const cash = worldState?.airline?.cash || 0;
  const repScore = worldState?.reputation?.currentScore || 0;
  const tier = calculateTier(repScore);
  const day = worldState?.dayNumber || 1;
  const fleetSize = worldState?.aircraft?.length || 0;
  const slotCount = Object.values(worldState?.slots || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  return (
    <div
      ref={panelRef}
      className="fixed z-[9999] select-none"
      style={{
        left: position.x,
        top: position.y,
        width: 320,
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="bg-gray-900/95 border border-purple-500/50 rounded-lg shadow-2xl backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-purple-500/30 drag-handle cursor-move">
          <div className="flex items-center gap-2">
            <span className="text-purple-400">≡</span>
            <span className="text-purple-400 font-bold text-sm">DEV CONSOLE</span>
            <Badge className="bg-purple-600 text-xs">CHEAT</Badge>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-gray-400 hover:text-white"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? "▼" : "▲"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
              onClick={() => setIsOpen(false)}
            >
              X
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="px-2 py-1 bg-gray-800/50 text-xs flex justify-between border-b border-gray-700">
          <span className="text-green-400">${(cash / 1000000).toFixed(1)}M</span>
          <span className="text-amber-400">T{tier} ({(repScore/1000).toFixed(1)}K)</span>
          <span className="text-blue-400">Day {day}</span>
          <span className="text-purple-400">{fleetSize} AC</span>
          <span className={slotCount > 0 ? "text-cyan-400" : "text-red-400"}>{slotCount} slots</span>
        </div>

        {/* Content */}
        {!isCollapsed && (
          <div className="p-2 space-y-3 max-h-[70vh] overflow-y-auto">
            {/* MONEY */}
            <Section symbol="$" title="Money" color="green">
              <div className="flex flex-wrap gap-1">
                <CheatBtn onClick={() => addMoney(1000000)}>+$1M</CheatBtn>
                <CheatBtn onClick={() => addMoney(10000000)}>+$10M</CheatBtn>
                <CheatBtn onClick={() => addMoney(100000000)}>+$100M</CheatBtn>
                <CheatBtn onClick={() => addMoney(-1000000)} variant="destructive">-$1M</CheatBtn>
              </div>
              <div className="flex gap-1 mt-1">
                <Input
                  placeholder="Set cash..."
                  value={cashInput}
                  onChange={(e) => setCashInput(e.target.value)}
                  className="h-7 text-xs bg-gray-800 border-gray-600"
                />
                <CheatBtn onClick={setMoney}>Set</CheatBtn>
              </div>
            </Section>

            {/* WINGS */}
            <Section symbol="◇" title="Wings" color="purple">
              <div className="text-xs text-purple-300 mb-1">
                Balance: {worldState?.wings?.balance || 20} Wings
              </div>
              <div className="flex flex-wrap gap-1">
                <CheatBtn onClick={() => addWings(10)}>+10</CheatBtn>
                <CheatBtn onClick={() => addWings(50)}>+50</CheatBtn>
                <CheatBtn onClick={() => addWings(100)}>+100</CheatBtn>
                <CheatBtn onClick={() => addWings(-10)} variant="destructive">-10</CheatBtn>
              </div>
              <div className="flex gap-1 mt-1">
                <Input
                  placeholder="Set wings..."
                  value={wingsInput}
                  onChange={(e) => setWingsInput(e.target.value)}
                  className="h-7 text-xs bg-gray-800 border-gray-600"
                />
                <CheatBtn onClick={setWings}>Set</CheatBtn>
              </div>
            </Section>

            {/* TIME */}
            <Section symbol="⏱" title="Time" color="blue">
              <div className="flex flex-wrap gap-1">
                <CheatBtn onClick={() => skipTime(1)}>+1 Day</CheatBtn>
                <CheatBtn onClick={() => skipTime(7)}>+1 Week</CheatBtn>
                <CheatBtn onClick={() => skipTime(30)}>+1 Month</CheatBtn>
                <CheatBtn onClick={triggerEndOfDay} style={{ backgroundColor: '#f59e0b' }}>End Day</CheatBtn>
              </div>
              <div className="flex gap-1 mt-1">
                <Input
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  className="h-7 text-xs bg-gray-800 border-gray-600"
                />
                <CheatBtn onClick={setDate}>Set</CheatBtn>
              </div>
            </Section>

            {/* REPUTATION */}
            <Section symbol="★" title="Reputation" color="amber">
              <div className="text-xs text-amber-300 mb-1">
                RepScore: {(worldState?.reputation?.currentScore || 0).toLocaleString()} | Tier {worldState?.reputation?.currentTier || 1}
              </div>
              <div className="flex flex-wrap gap-1">
                <CheatBtn onClick={() => adjustRepScore(500)}>+500</CheatBtn>
                <CheatBtn onClick={() => adjustRepScore(1000)}>+1K</CheatBtn>
                <CheatBtn onClick={() => adjustRepScore(-500)} variant="destructive">-500</CheatBtn>
                <CheatBtn onClick={() => setRepScore(20000)}>Max</CheatBtn>
              </div>
              <div className="flex gap-1 mt-1">
                <Input
                  placeholder="RepScore..."
                  value={repScoreInput}
                  onChange={(e) => setRepScoreInput(e.target.value)}
                  className="h-7 text-xs bg-gray-800 border-gray-600 w-24"
                />
                <CheatBtn onClick={setRepScoreFromInput}>Set</CheatBtn>
              </div>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map(t => (
                  <CheatBtn
                    key={t}
                    onClick={() => setTier(t)}
                    className={`flex-1 ${worldState?.reputation?.currentTier === t ? 'bg-amber-600' : ''}`}
                  >
                    T{t}
                  </CheatBtn>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                T2:1K | T3:3K | T4:7K | T5:15K
              </div>
            </Section>

            {/* FLEET */}
            <Section symbol="F" title="Fleet" color="purple">
              <div className="flex flex-wrap gap-1">
                <CheatBtn onClick={() => addTestAircraft("E175")}>+E175</CheatBtn>
                <CheatBtn onClick={() => addTestAircraft("A320")}>+A320</CheatBtn>
                <CheatBtn onClick={() => addTestAircraft("B737-800")}>+737</CheatBtn>
                <CheatBtn onClick={() => addTestAircraft("A380")}>+A380</CheatBtn>
              </div>
              <CheatBtn onClick={removeAllAircraft} variant="destructive" className="w-full mt-1">
                Remove All Aircraft
              </CheatBtn>
            </Section>

            {/* SLOTS */}
            <Section symbol="◉" title="Slots" color="cyan">
              <CheatBtn onClick={resetSlotsToDefault} className="w-full mb-1 bg-cyan-900/50 border-cyan-500/30">
                Fix/Reset Starter Slots
              </CheatBtn>
              <div className="flex gap-1">
                <Input
                  placeholder="KSMF"
                  value={slotAirport}
                  onChange={(e) => setSlotAirport(e.target.value)}
                  className="h-7 text-xs bg-gray-800 border-gray-600 w-20"
                />
                <CheatBtn onClick={grantSlot}>Grant Slot</CheatBtn>
              </div>
              <CheatBtn
                onClick={unlockAllAirports}
                className="w-full mt-1"
                style={{
                  backgroundColor: !worldState?.airportsGated ? '#22c55e' : '#333',
                  borderColor: !worldState?.airportsGated ? '#22c55e' : '#666'
                }}
              >
                {!worldState?.airportsGated ? '[ON] All Airports UNLOCKED' : 'Unlock All Airports'}
              </CheatBtn>
              <CheatBtn
                onClick={() => setWorldState(prev => ({ ...prev, tierBypass: !prev.tierBypass }))}
                className="w-full mt-1"
                style={{
                  backgroundColor: worldState?.tierBypass ? '#22c55e' : '#333',
                  borderColor: worldState?.tierBypass ? '#22c55e' : '#666'
                }}
              >
                {worldState?.tierBypass ? '[ON] Tier Bypass' : 'Tier Bypass OFF'}
              </CheatBtn>
              <div className="flex gap-1 mt-1">
                <Input
                  placeholder="KSMF"
                  value={triggerAirport}
                  onChange={(e) => setTriggerAirport(e.target.value)}
                  className="h-7 text-xs bg-gray-800 border-gray-600 w-20"
                />
                <CheatBtn onClick={triggerNegotiation} className="flex-1">Trigger Negotiation</CheatBtn>
                <CheatBtn onClick={triggerHubAuction} className="flex-1" style={{ backgroundColor: '#7c3aed' }}>Trigger Hub Auction</CheatBtn>
              </div>
            </Section>

            {/* SIMULATION */}
            <Section symbol="⚡" title="Simulation" color="orange">
              {/* Speed Controls */}
              <div className="text-xs text-gray-400 mb-1">Game Speed:</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {[1, 2, 4, 8, 16, 32, 64].map((speed) => (
                  <CheatBtn
                    key={speed}
                    onClick={() => setWorldState(prev => ({ ...prev, gameSpeed: speed, isPaused: false }))}
                    className={worldState?.gameSpeed === speed ? 'bg-orange-600 border-orange-500' : ''}
                  >
                    {speed}x
                  </CheatBtn>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                <CheatBtn onClick={forceFlightComplete}>Complete Flight</CheatBtn>
                <CheatBtn onClick={triggerRandomDelay}>Random Delay</CheatBtn>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                <CheatBtn onClick={triggerCrewIllegality} variant="destructive">Crew Illegal</CheatBtn>
                <CheatBtn onClick={triggerWeatherEvent} variant="destructive">Weather Event</CheatBtn>
              </div>

              {/* Trailer Mode - Mass flights for video capture */}
              <div className="text-xs text-gray-400 mt-2 mb-1">Video/Trailer Capture:</div>
              <CheatBtn
                onClick={() => {
                  const gameTime = new Date(worldState?.gameTime || Date.now());
                  const trailerFlights = generateTrailerFlights(gameTime);
                  setWorldState(prev => ({
                    ...prev,
                    flights: [...(prev.flights || []), ...trailerFlights],
                  }));
                  debug(`[CHEAT] Added ${trailerFlights.length} trailer flights!`);
                }}
                className="w-full bg-pink-900/50 border-pink-500/30 text-pink-300 mb-1"
              >
                + 175 Random Flights (Trailer Mode)
              </CheatBtn>
              <CheatBtn
                onClick={() => {
                  setWorldState(prev => ({
                    ...prev,
                    flights: (prev.flights || []).filter(f => !f.isTrailerFlight),
                  }));
                  debug('[CHEAT] Cleared all trailer flights');
                }}
                variant="destructive"
                className="w-full"
              >
                Clear Trailer Flights
              </CheatBtn>

              {/* Test Routes UI */}
              <div className="text-xs text-gray-400 mt-2 mb-1">Routes UI Test:</div>
              <CheatBtn
                onClick={() => {
                  const hub = worldState?.airline?.hub || 'KPDX';
                  const gameTime = new Date(worldState?.gameTime || Date.now());

                  // Test destinations
                  const dests = ['KSEA', 'KBOI', 'KRDM', 'KSMF', 'KLAS', 'KPHX', 'KDEN', 'KSFO'];

                  // Create 2 aircraft for testing
                  const testAircraft = [];
                  for (let i = 0; i < 4; i++) {
                    const ac = {
                      id: `TEST-AC-${Date.now()}-${i}`,
                      type: 'E175',
                      model: 'E175',
                      registration: `N${100 + i}TS`,
                      capacity: 76,
                      range: 2000,
                      cruiseSpeed: 450,
                      status: i < 2 ? 'available' : 'available', // All available
                      location: hub,
                      homeBase: hub,
                    };
                    testAircraft.push(ac);
                  }

                  // Create routes in different states
                  const testRoutes = [
                    // 2 UNASSIGNED routes (no aircraft)
                    { id: `RT-UNAS-1`, origin: hub, destination: dests[0], aircraftId: null, status: 'unassigned' },
                    { id: `RT-UNAS-2`, origin: hub, destination: dests[1], aircraftId: null, status: 'unassigned' },
                    // 2 READY routes (aircraft assigned, not flying)
                    { id: `RT-READY-1`, origin: hub, destination: dests[2], aircraftId: testAircraft[0].id, status: 'ready', scheduledDeparture: new Date(gameTime.getTime() + 20*60000).toISOString() },
                    { id: `RT-READY-2`, origin: hub, destination: dests[3], aircraftId: testAircraft[1].id, status: 'ready', scheduledDeparture: new Date(gameTime.getTime() + 25*60000).toISOString() },
                    // 2 routes that will be IN FLIGHT
                    { id: `RT-FLY-1`, origin: hub, destination: dests[4], aircraftId: testAircraft[2].id, status: 'in_flight' },
                    { id: `RT-FLY-2`, origin: hub, destination: dests[5], aircraftId: testAircraft[3].id, status: 'in_flight' },
                  ];

                  // Create in-flight flights for routes 5 & 6
                  const testFlights = [
                    {
                      id: `FLT-TEST-1`,
                      flightNumber: 'TS101',
                      origin: hub,
                      destination: dests[4],
                      aircraftId: testAircraft[2].id,
                      routeId: 'RT-FLY-1',
                      phase: 'cruise',
                      status: 'in_flight',
                      distanceFraction: 0.4,
                      progress: 0.4,
                      departedAt: new Date(gameTime.getTime() - 30*60000).toISOString(),
                      scheduledDeparture: new Date(gameTime.getTime() - 30*60000).toISOString(),
                      scheduledArrival: new Date(gameTime.getTime() + 60*60000).toISOString(),
                    },
                    {
                      id: `FLT-TEST-2`,
                      flightNumber: 'TS102',
                      origin: hub,
                      destination: dests[5],
                      aircraftId: testAircraft[3].id,
                      routeId: 'RT-FLY-2',
                      phase: 'cruise',
                      status: 'in_flight',
                      distanceFraction: 0.7,
                      progress: 0.7,
                      departedAt: new Date(gameTime.getTime() - 60*60000).toISOString(),
                      scheduledDeparture: new Date(gameTime.getTime() - 60*60000).toISOString(),
                      scheduledArrival: new Date(gameTime.getTime() + 20*60000).toISOString(),
                    },
                  ];

                  // Update aircraft status for in-flight ones
                  testAircraft[2].status = 'in_flight';
                  testAircraft[3].status = 'in_flight';

                  setWorldState(prev => ({
                    ...prev,
                    aircraft: [...(prev.aircraft || []), ...testAircraft],
                    routes: [...(prev.routes || []), ...testRoutes],
                    flights: [...(prev.flights || []), ...testFlights],
                  }));

                  debug('[CHEAT] Created test routes: 2 unassigned, 2 ready, 2 in-flight');
                }}
                className="w-full bg-cyan-900/50 border-cyan-500/30 text-cyan-300"
              >
                Populate Test Routes (All States)
              </CheatBtn>
            </Section>

            {/* EOS (Elite Operator Status) */}
            <Section symbol="E" title="EOS System" color="purple">
              {(() => {
                const eosStatus = getEOSStatus(worldState);
                return (
                  <>
                    {/* Status Display */}
                    <div className="text-xs mb-2 p-2 rounded" style={{ backgroundColor: "rgba(139, 92, 246, 0.1)" }}>
                      <div className="flex justify-between">
                        <span>Status:</span>
                        <span className={eosStatus?.hasEOS ? "text-green-400" : "text-gray-400"}>
                          {eosStatus?.hasEOS ? "ACTIVE" : eosStatus?.inCooldown ? `COOLDOWN (${eosStatus?.cooldownRemaining}d)` : "Not Earned"}
                        </span>
                      </div>
                      {!eosStatus?.hasEOS && !eosStatus?.inCooldown && (
                        <div className="flex justify-between mt-1">
                          <span>Progress:</span>
                          <span className="text-purple-300">{eosStatus?.consecutiveDays || 0}/{EOS_REQUIREMENTS.QUALIFICATION_DAYS} days</span>
                        </div>
                      )}
                      <div className="flex justify-between mt-1">
                        <span>Flights/Day:</span>
                        <span className="text-cyan-300">{worldState?.stats?.dailyFlights || 0}/{EOS_REQUIREMENTS.MIN_FLIGHTS_PER_DAY}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span>Rolls:</span>
                        <span className="text-amber-400">{eosStatus?.stats?.rareAircraftRolls || 0}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span>Rare Owned:</span>
                        <span className="text-purple-400">{eosStatus?.stats?.rareAircraftOwned || 0}</span>
                      </div>
                    </div>

                    {/* EOS Controls - Two Grant Buttons */}
                    <div className="text-xs text-gray-400 mb-1">Grant EOS:</div>
                    <div className="flex flex-wrap gap-1">
                      <CheatBtn
                        onClick={() => {
                          setWorldState(prev => ({
                            ...prev,
                            eos: {
                              ...(prev.eos || createEOSState()),
                              hasEOS: true,
                              grantedAt: new Date().toISOString(),
                              timesEarned: (prev.eos?.timesEarned || 0) + 1,
                              lockoutUntil: null,
                            },
                          }));
                          debug("[CHEAT] EOS granted (flag only)!");
                        }}
                        className="flex-1 bg-purple-900/50 border-purple-500/30 text-purple-300"
                      >
                        Flag Only
                      </CheatBtn>
                      <CheatBtn
                        onClick={() => {
                          setWorldState(prev => ({
                            ...prev,
                            // Set Tier 5 reputation
                            reputation: {
                              ...prev.reputation,
                              currentScore: Math.max(prev.reputation?.currentScore || 0, 15000),
                              currentTier: 5,
                            },
                            // Set EOS state with all requirements met
                            eos: {
                              ...(prev.eos || createEOSState()),
                              hasEOS: true,
                              consecutiveDays: EOS_REQUIREMENTS.QUALIFICATION_DAYS,
                              grantedAt: new Date().toISOString(),
                              timesEarned: (prev.eos?.timesEarned || 0) + 1,
                              lockoutUntil: null,
                            },
                            // Clear any blocking stats
                            stats: {
                              ...prev.stats,
                              dailyCancellations: 0,
                              dailyFlights: Math.max(prev.stats?.dailyFlights || 0, 20),
                              onTimeFlights: Math.max(prev.stats?.onTimeFlights || 0, 100),
                              completedFlights: Math.max(prev.stats?.completedFlights || 0, 100),
                            },
                            // Grant speed unlocks (permanent)
                            unlocks: {
                              ...(prev.unlocks || {}),
                              speed4x: true,
                              speed12x: true,
                            },
                          }));
                          debug("[CHEAT] EOS granted + speed unlocks (4x, 12x)!");
                        }}
                        className="flex-1 bg-green-900/50 border-green-500/30 text-green-300"
                      >
                        Full (All Reqs)
                      </CheatBtn>
                      <CheatBtn
                        onClick={() => {
                          setWorldState(prev => ({
                            ...prev,
                            eos: {
                              ...(prev.eos || createEOSState()),
                              hasEOS: false,
                              consecutiveDays: 0,
                              lostAt: new Date().toISOString(),
                              lockoutUntil: null,
                            },
                          }));
                          debug("[CHEAT] EOS removed!");
                        }}
                        variant="destructive"
                        className="flex-1"
                      >
                        Remove EOS
                      </CheatBtn>
                      <CheatBtn
                        onClick={() => {
                          setWorldState(prev => ({
                            ...prev,
                            eos: {
                              ...(prev.eos || createEOSState()),
                              consecutiveDays: 4,
                              qualificationStart: new Date().toISOString(),
                            },
                            unlocks: {
                              ...(prev.unlocks || {}),
                              speed4x: true,
                            },
                          }));
                          debug("[CHEAT] EOS streak set to Day 4 + 4x speed unlocked!");
                        }}
                        className="flex-1 bg-yellow-900/50 border-yellow-500/30 text-yellow-300"
                      >
                        Day 4 (4x Speed)
                      </CheatBtn>
                    </div>

                    {/* Roll Controls */}
                    <div className="flex flex-wrap gap-1 mt-1">
                      <CheatBtn
                        onClick={() => {
                          // Force a roll even without EOS (cheat mode)
                          const rollResult = rollRareAircraft();
                          debug("[CHEAT] Forced roll:", rollResult);

                          // Create the rare aircraft
                          const rareAircraft = {
                            id: `RARE-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                            eosId: rollResult.aircraft.id, // EOS ID for anomalous behavior matching
                            type: rollResult.aircraft.id,
                            model: rollResult.aircraft.id,
                            name: rollResult.aircraft.name,
                            registration: `N${Math.floor(Math.random() * 900) + 100}${rollResult.rarity.toUpperCase().slice(0, 2)}`,
                            rarity: rollResult.rarity,
                            capacity: rollResult.aircraft.capacity,
                            range: rollResult.aircraft.range,
                            cruiseSpeed: rollResult.aircraft.cruiseSpeed,
                            fuelBurn: rollResult.aircraft.fuelBurn,
                            monthlyMaintenance: rollResult.aircraft.monthlyMaintenance,
                            specialAbilities: rollResult.aircraft.specialAbilities,
                            tradeoffs: rollResult.aircraft.tradeoffs,
                            description: rollResult.aircraft.description,
                            icon: rollResult.aircraft.icon,
                            status: "available",
                            location: worldState.airline?.hub || "KPDX",
                            homeBase: worldState.airline?.hub || "KPDX",
                            flightHours: 0,
                            cycles: 0,
                            isRareAircraft: true,
                            acquiredAt: new Date().toISOString(),
                          };

                          setWorldState(prev => ({
                            ...prev,
                            aircraft: [...prev.aircraft, rareAircraft],
                            eos: {
                              ...(prev.eos || createEOSState()),
                              rareAircraftRolls: (prev.eos?.rareAircraftRolls || 0) + 1,
                              rareAircraftOwned: [
                                ...(prev.eos?.rareAircraftOwned || []),
                                { aircraftId: rollResult.aircraft.id, rarity: rollResult.rarity, earnedAt: new Date().toISOString() },
                              ],
                            },
                          }));

                          alert(`RARE ROLL: ${rollResult.rarity.toUpperCase()}!\n\n${rollResult.aircraft.name}\n\n${rollResult.aircraft.description}\n\nRoll: ${rollResult.rollPercent}%`);
                        }}
                        className="flex-1 bg-amber-900/50 border-amber-500/30 text-amber-300"
                      >
                        Force Roll
                      </CheatBtn>
                    </div>

                    {/* Add specific rarity aircraft */}
                    <div className="text-xs text-gray-500 mt-2 mb-1">Add Specific Rarity:</div>
                    <div className="flex flex-wrap gap-1">
                      <CheatBtn
                        onClick={() => {
                          const relic = RARE_AIRCRAFT.relic[0];
                          const aircraft = {
                            id: `RARE-${Date.now()}`,
                            eosId: relic.id,
                            type: relic.id,
                            model: relic.id,
                            name: relic.name,
                            registration: `N${Math.floor(Math.random() * 900) + 100}RL`,
                            rarity: "relic",
                            ...relic,
                            status: "available",
                            location: worldState.airline?.hub || "KPDX",
                            homeBase: worldState.airline?.hub || "KPDX",
                            isRareAircraft: true,
                          };
                          setWorldState(prev => ({
                            ...prev,
                            aircraft: [...prev.aircraft, aircraft],
                          }));
                        }}
                        className="flex-1 text-cyan-300"
                      >
                        +Relic
                      </CheatBtn>
                      <CheatBtn
                        onClick={() => {
                          const legendary = RARE_AIRCRAFT.legendary[0];
                          const aircraft = {
                            id: `RARE-${Date.now()}`,
                            eosId: legendary.id,
                            type: legendary.id,
                            model: legendary.id,
                            name: legendary.name,
                            registration: `N${Math.floor(Math.random() * 900) + 100}LG`,
                            rarity: "legendary",
                            ...legendary,
                            status: "available",
                            location: worldState.airline?.hub || "KPDX",
                            homeBase: worldState.airline?.hub || "KPDX",
                            isRareAircraft: true,
                          };
                          setWorldState(prev => ({
                            ...prev,
                            aircraft: [...prev.aircraft, aircraft],
                          }));
                        }}
                        className="flex-1 text-amber-300"
                      >
                        +Legendary
                      </CheatBtn>
                      <CheatBtn
                        onClick={() => {
                          const mythic = RARE_AIRCRAFT.mythic[0];
                          const aircraft = {
                            id: `RARE-${Date.now()}`,
                            eosId: mythic.id,
                            type: mythic.id,
                            model: mythic.id,
                            name: mythic.name,
                            registration: `N${Math.floor(Math.random() * 900) + 100}MY`,
                            rarity: "mythic",
                            ...mythic,
                            status: "available",
                            location: worldState.airline?.hub || "KPDX",
                            homeBase: worldState.airline?.hub || "KPDX",
                            isRareAircraft: true,
                          };
                          setWorldState(prev => ({
                            ...prev,
                            aircraft: [...prev.aircraft, aircraft],
                          }));
                        }}
                        className="flex-1 text-purple-300"
                      >
                        +Mythic
                      </CheatBtn>
                    </div>

                    {/* Specific Aircraft Selector */}
                    <div className="text-xs text-gray-500 mt-3 mb-1">Grant Specific Aircraft ({eosCollectibles.total} total):</div>
                    <div className="flex gap-1">
                      <select
                        value={selectedEOSAircraft}
                        onChange={(e) => setSelectedEOSAircraft(e.target.value)}
                        className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200"
                        style={{ maxWidth: "180px" }}
                      >
                        <option value="">-- Select Aircraft --</option>
                        <optgroup label={`RARE (${eosCollectibles.rare.length})`}>
                          {eosCollectibles.rare.map(ac => (
                            <option key={ac.id} value={ac.id}>{ac.model}</option>
                          ))}
                        </optgroup>
                        <optgroup label={`RELIC (${eosCollectibles.relic.length})`}>
                          {eosCollectibles.relic.map(ac => (
                            <option key={ac.id} value={ac.id}>{ac.model}</option>
                          ))}
                        </optgroup>
                        <optgroup label={`ANOMALOUS (${eosCollectibles.anomalous.length})`}>
                          {eosCollectibles.anomalous.map(ac => (
                            <option key={ac.id} value={ac.id}>{ac.model}</option>
                          ))}
                        </optgroup>
                        <optgroup label={`LEGENDARY (${eosCollectibles.legendary.length})`}>
                          {eosCollectibles.legendary.map(ac => (
                            <option key={ac.id} value={ac.id}>{ac.model}</option>
                          ))}
                        </optgroup>
                        <optgroup label={`MYTHIC (${eosCollectibles.mythic.length})`}>
                          {eosCollectibles.mythic.map(ac => (
                            <option key={ac.id} value={ac.id}>{ac.model}</option>
                          ))}
                        </optgroup>
                      </select>
                      <CheatBtn
                        onClick={() => {
                          if (!selectedEOSAircraft) return;
                          const acData = AIRCRAFT_DATABASE[selectedEOSAircraft];
                          if (!acData) return;

                          const aircraft = {
                            id: `EOS-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                            eosId: selectedEOSAircraft, // EOS ID for anomalous behavior matching
                            type: selectedEOSAircraft,
                            model: selectedEOSAircraft,
                            aircraftId: selectedEOSAircraft,
                            name: acData.model,
                            registration: `N${Math.floor(Math.random() * 900) + 100}${acData.rarity?.slice(0, 2).toUpperCase() || "EO"}`,
                            rarity: acData.rarity,
                            capacity: acData.capacity,
                            range: acData.range,
                            cruiseSpeed: acData.cruiseSpeed,
                            fuelBurn: acData.fuelBurn,
                            monthlyMaintenance: acData.operatingCost,
                            tagline: acData.tagline,
                            status: "available",
                            location: worldState.airline?.hub || "KPDX",
                            homeBase: worldState.airline?.hub || "KPDX",
                            flightHours: 0,
                            cycles: 0,
                            isRareAircraft: true,
                            acquiredAt: new Date().toISOString(),
                          };

                          setWorldState(prev => ({
                            ...prev,
                            aircraft: [...prev.aircraft, aircraft],
                            eos: {
                              ...(prev.eos || createEOSState()),
                              rareAircraftOwned: [
                                ...(prev.eos?.rareAircraftOwned || []),
                                { aircraftId: selectedEOSAircraft, rarity: acData.rarity, earnedAt: new Date().toISOString() },
                              ],
                            },
                          }));

                          debug(`[CHEAT] Granted EOS aircraft: ${acData.model} (${acData.rarity})`);
                        }}
                        disabled={!selectedEOSAircraft}
                        className="bg-emerald-900/50 border-emerald-500/30 text-emerald-300"
                      >
                        Grant
                      </CheatBtn>
                      <CheatBtn
                        onClick={() => {
                          if (!selectedEOSAircraft) return;
                          const acData = AIRCRAFT_DATABASE[selectedEOSAircraft];
                          if (!acData) return;
                          // Prepare aircraft data for reveal animation
                          setTestRollAircraft({
                            id: selectedEOSAircraft,
                            name: acData.model,
                            model: acData.model,
                            rarity: acData.rarity,
                            image: acData.image,
                            capacity: acData.capacity,
                            range: acData.range,
                            flavorText: acData.tagline,
                          });
                          setShowTestRoll(true);
                        }}
                        disabled={!selectedEOSAircraft}
                        className="bg-purple-900/50 border-purple-500/30 text-purple-300"
                      >
                        Test Reveal
                      </CheatBtn>
                    </div>

                    {/* Info */}
                    <div className="text-xs text-gray-500 mt-2">
                      EOS: {EOS_REQUIREMENTS.QUALIFICATION_DAYS}d @ T5, {EOS_REQUIREMENTS.MIN_FLIGHTS_PER_DAY} flights/day | Roll: {(ROLL_PROBABILITIES.LEGENDARY * 100).toFixed(1)}%/{(ROLL_PROBABILITIES.ANOMALOUS * 100).toFixed(1)}%/{(ROLL_PROBABILITIES.MYTHIC * 100).toFixed(1)}%
                    </div>
                  </>
                );
              })()}
            </Section>

            {/* STATE */}
            <Section symbol="◈" title="State" color="gray">
              <div className="flex gap-1">
                <CheatBtn onClick={exportState} className="flex-1">
                  Export
                </CheatBtn>
                <CheatBtn onClick={importState} className="flex-1">
                  Import
                </CheatBtn>
              </div>
              <CheatBtn onClick={resetWorldState} variant="destructive" className="w-full mt-1">
                ! Reset to New Game
              </CheatBtn>
            </Section>

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-700">
              Press <kbd className="px-1 bg-gray-700 rounded">`</kbd> to toggle
            </div>
          </div>
        )}
      </div>

      {/* Test Roll Animation Overlay */}
      {showTestRoll && testRollAircraft && (
        testRollAircraft.rarity === "mythic" ? (
          <MythicReveal
            aircraft={testRollAircraft}
            onClaim={() => {
              setShowTestRoll(false);
              setTestRollAircraft(null);
            }}
          />
        ) : (
          // Standard reveal for non-mythic
          <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[10000]">
            <div className="text-center">
              <div
                className="p-8 rounded-xl mx-auto max-w-md animate-in zoom-in duration-500"
                style={{
                  backgroundColor: "#0c0c0c",
                  border: `3px solid ${
                    testRollAircraft.rarity === "legendary" ? "#ef4444" :
                    testRollAircraft.rarity === "anomalous" ? "#8b5cf6" :
                    testRollAircraft.rarity === "relic" ? "#06b6d4" : "#22c55e"
                  }`,
                  boxShadow: `0 0 60px ${
                    testRollAircraft.rarity === "legendary" ? "#ef4444" :
                    testRollAircraft.rarity === "anomalous" ? "#8b5cf6" :
                    testRollAircraft.rarity === "relic" ? "#06b6d4" : "#22c55e"
                  }`,
                }}
              >
                <div
                  className="text-sm font-bold mb-2 uppercase tracking-widest"
                  style={{
                    color: testRollAircraft.rarity === "legendary" ? "#ef4444" :
                           testRollAircraft.rarity === "anomalous" ? "#8b5cf6" :
                           testRollAircraft.rarity === "relic" ? "#06b6d4" : "#22c55e"
                  }}
                >
                  {testRollAircraft.rarity}
                </div>
                <h2
                  className="text-3xl font-bold mb-4"
                  style={{
                    color: testRollAircraft.rarity === "legendary" ? "#ef4444" :
                           testRollAircraft.rarity === "anomalous" ? "#8b5cf6" :
                           testRollAircraft.rarity === "relic" ? "#06b6d4" : "#22c55e"
                  }}
                >
                  {testRollAircraft.name}
                </h2>
                {testRollAircraft.image && (
                  <img
                    src={testRollAircraft.image}
                    alt={testRollAircraft.name}
                    className="w-64 h-40 object-contain mx-auto mb-4"
                  />
                )}
                {testRollAircraft.flavorText && (
                  <p className="text-sm italic text-gray-400 mb-4">"{testRollAircraft.flavorText}"</p>
                )}
                <button
                  onClick={() => {
                    setShowTestRoll(false);
                    setTestRollAircraft(null);
                  }}
                  className="mt-4 px-8 py-3 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function Section({ symbol, title, color, children }) {
  const colors = {
    green: "text-green-400 border-green-500/30",
    blue: "text-blue-400 border-blue-500/30",
    amber: "text-amber-400 border-amber-500/30",
    purple: "text-purple-400 border-purple-500/30",
    cyan: "text-cyan-400 border-cyan-500/30",
    orange: "text-orange-400 border-orange-500/30",
    gray: "text-gray-400 border-gray-500/30",
  };

  return (
    <div className={`border rounded-lg p-2 ${colors[color]}`}>
      <div className="flex items-center gap-1 mb-2">
        <span className={colors[color].split(" ")[0]}>{symbol}</span>
        <span className="text-xs font-medium">{title}</span>
      </div>
      {children}
    </div>
  );
}

function CheatBtn({ children, onClick, variant = "default", className = "" }) {
  const variants = {
    default: "bg-gray-700 hover:bg-gray-600 text-white",
    destructive: "bg-red-900/50 hover:bg-red-800/50 text-red-300 border-red-500/30",
  };

  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 text-xs rounded border border-gray-600 transition-colors flex items-center justify-center ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}
